import React, { useContext, useMemo, useState, useCallback } from "react";
import moment from "moment";
import _ from "lodash";
import styled, { useTheme } from "styled-components";
import { Flex } from "reflexbox";
import { useQuery } from "../Hooks";
import { PageWrapper } from "../UI/Grid";
import { Body } from "../UI/Text";
import Table from "../UI/Table";
import Modal from "../UI/Modal";
import Button from "../UI/Button";
import Card from "../UI/Card";
import AlertContext from "../../context/alert/AlertContext";
import IssueReportForm from "./IssueReportForm";
import * as issueReportRequests from "../../requests/issueReportManagementRequests";

const { getIssueReports, deleteIssueReport } = issueReportRequests;

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const IssueReportsPage = () => {
  const theme = useTheme();
  const { showSuccessAlert } = useContext(AlertContext);

  const [show, setShow] = useState(false);
  const [issueReportId, setIssueReportId] = useState("create");

  const { data, isLoading, refetchQuery, setData } = useQuery({
    queryID: "issueReportsPage",
    request: getIssueReports,
  });

  const { count = 0, rows: issueReports = [] } = data || {};

  const currentIssueReport = useMemo(
    () => _.find(issueReports, (__) => __.id === issueReportId),
    [issueReports, issueReportId]
  );
  const onDelete = useCallback(
    async (e, id) => {
      e.stopPropagation();
      const { rows } = data;
      const newRows = [...rows];
      _.remove(newRows, (row) => row.id === id);
      setData({ rows: newRows, count: count - 1 });
      setShow(false);
      await deleteIssueReport({ id });

      showSuccessAlert(`Successfully deleted IssueReport with the ID of ${id}`);
    },
    [count, data, setData, showSuccessAlert]
  );

  const onRowClick = (row) => {
    setIssueReportId(row.original.id);
    setShow(true);
  };

  const statusLabels = [
    "To Do",
    "In Progress",
    "Ready For QA",
    "Awaiting Deployment",
    "In Production",
  ];

  const priorityLabels = ["Very Low", "Low", "Medium", "High", "Very High"];

  const columns = useMemo(() => {
    return [
      {
        id: "title",
        data: (d) => (
          <Body center ellipsis>
            {d?.id || "—"}
          </Body>
        ),
        header: "Title",
        size: { md: 2, sm: 1, xs: 1 },
      },
      {
        id: "report",
        data: (d) => <Body>{d?.report || "—"}</Body>,
        header: "Report",
        size: { md: 5, sm: 3, xs: 2 },
      },

      {
        id: "priority",
        data: (d) => (
          <Body center ellipsis>
            {priorityLabels[d?.priority] || "—"}
          </Body>
        ),
        header: "Priority",
        size: { md: 1, sm: 1, xs: 1 },
      },
      {
        id: "status",
        data: (d) => (
          <Body center ellipsis>
            {statusLabels[d?.status] || "—"}
          </Body>
        ),
        header: "Status",
        size: { md: 1, sm: 1, xs: 1 },
      },
      {
        id: "createdAt",
        data: (d) => (
          <Body center ellipsis>
            {moment(d?.createdAt).format("MM/DD/YYYY") || "—"}
          </Body>
        ),
        header: "Created",
        size: { md: 1, sm: 1, xs: 1 },
      },

      {
        display: (props) => {
          let issueReport = props.cell.row.original;

          return (
            <ButtonsWrapper id="actions">
              <Button
                size="small"
                color={theme.secondary}
                reverse
                iconName="faPencil"
                onClick={(e) => onRowClick(props.cell.row, e)}
              />
              <Button
                size="small"
                color={theme.secondary}
                reverse
                iconName="faTrashCan"
                onClick={(e) => onDelete(e, issueReport.id)}
              />
            </ButtonsWrapper>
          );
        },
        header: "Actions",
        sort: false,
        size: { lg: 1.5, md: 2, sm: 0, xs: 0 },
      },
    ];
  }, [onDelete, priorityLabels, statusLabels, theme.secondary]);

  return (
    <PageWrapper>
      <Modal show={show} setShow={setShow}>
        <Card>
          <IssueReportForm
            data={data}
            count={count}
            issueReportId={issueReportId}
            setIssueReportId={setIssueReportId}
            currentIssueReport={currentIssueReport}
            setShow={setShow}
            setData={setData}
          />
        </Card>
      </Modal>
      <Table
        title="Issue Reports"
        data={issueReports}
        createButtonText="Create Issue Report"
        createLink={() => {
          setIssueReportId("create");
          setShow(true);
        }}
        createButtonIcon="faBug"
        hideToggle
        hideRefetch={false}
        refetchData={refetchQuery}
        onRowClick={onRowClick}
        columns={columns}
        isLoading={isLoading}
        totalCount={count}
        backButtonLink="/Administration"
      />
    </PageWrapper>
  );
};

export default IssueReportsPage;
